import React from 'react';
import { observer } from 'mobx-react';

import useStores from '@stores/useStores';
import { FeatureGateBadge } from './featureGateBadge';
import { useUpgradeNavigation } from '../../common/useUpgradeNavigation';
import { ServerFeatures } from '@stores/features';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { Flex } from '@ui-library/flex';
import { TrialBaseBadge } from '@components/trial/common/trialBaseBadge';
import { buildDefaultFeatureCTAs, featuresRegistry } from '../../featuresRegistry';

interface TrialFeatureTagProps {
  featureKey?: keyof ServerFeatures;
  featureName?: string;
  compact?: boolean;
  variant?: 'default' | 'daysRemaining';
}

export const FeatureTrialTag: React.FC<TrialFeatureTagProps> = observer(
  ({ featureKey, featureName: featureNameProp, compact, variant = 'default', children }) => {
    const { trialStore } = useStores();
    const onUpgrade = useUpgradeNavigation();
    const isPLGTrialEnabled = experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrial);
    const trialInfo = featureKey ? trialStore.getFeatureTrialInfo(featureKey) : null;
    if (!isPLGTrialEnabled || !trialInfo) {
      return children ? <>{children}</> : null;
    }

    const isTrialActive = trialInfo.trial.status === 'in_progress';

    const registryEntry = featureKey ? featuresRegistry[featureKey] : undefined;
    const featureName = featureNameProp ?? registryEntry?.name ?? 'This feature';
    const targetPlanName = trialInfo.trial.definition.plan?.displayName ?? '';

    const defaultTrialBadge = buildDefaultFeatureCTAs({
      featureName,
      currentPlan: '',
      targetPlan: targetPlanName,
      currentLimit: 0,
    }).trialBadge;

    const trialBadgeDescription = registryEntry?.ctas?.trialBadge ?? defaultTrialBadge;
    const badge =
      variant === 'daysRemaining' ? (
        <TrialBaseBadge
          label={`Trial - ${trialStore.trialDaysRemaining}d left`}
          trialPhase={trialInfo.phase}
          compact={compact}
        />
      ) : (
        <FeatureGateBadge
          compact={compact}
          isTrialActive={isTrialActive}
          daysRemaining={trialInfo.daysRemaining}
          trialPhase={trialInfo.phase}
          trialType={trialInfo.trial.definition.trialType}
          description={trialBadgeDescription}
          onUpgrade={onUpgrade}
        />
      );

    return (
      <Flex gap="6px" alignItemsCenter>
        {children}
        {badge}
      </Flex>
    );
  },
);
